import Application from "../../Application";
import { IGenericCodebookItem } from "../../codebooks/model/GenericCodebookItem";
import withStore, {
  TLoadingAwarePresenter,
  TPresentable
} from "../../core/presenter/withStore";
import { IOrganisationIndex } from "../../organisations/model/OrganisationIndex";
import { IAttachmentType } from "../../attachmentType/model/AttachmentType";
import { ICaseDecision } from "../model/CaseDecision";
import { IOrganisation } from "../../organisations/model/Organisation";

import AttachmentTypePresentationModel, {
  TAttachmentTypePresentationModel
} from "../../organisations/presenter/model/AttachmentTypePresentationModel";
import CaseDecisionPresentationModel, {
  TCaseDecisionPresentationModel
} from "./model/CaseDecisionPresentationModel";
import { IUser } from "../../user/model/User";
import AttachmentPresentationModel, {
  TAttachmentPresentationModel
} from "../../caseLawDocuments/presenter/model/AttachmentPresentationModel";
import { IAttachment } from "../../caseLawDocuments/model/Attachment";
import { ICourtCase } from "../../caseLawDocuments/model/CourtCase";
import ArchiveCaseDecisionInteractor from "../interactor/ArchiveCaseDecisionInteractor";
import FindCaseDecisionInteractor from "../interactor/FindCaseDecisionInteractor";
import OrganisationIndexPresentationModel, {
  TOrganisationIndexPresentationModel
} from "../../organisations/presenter/model/OrganisationIndexPresentationModel";
import {
  IDocumentType,
  TDocumentType
} from "../../documentTypes/model/DocumentType";
import RemoveCaseDecisionInteractor from "../interactor/RemoveCaseDecisionInteractor";
import ShowSuccessMessageInteractor from "../../core/interactor/notifications/ShowSuccessMessageInteractor";
import PublishCaseDecisionInteractor from "../interactor/PublishCaseDecisionInteractor";
import RevertCaseDecisionStatusInteractor from "../interactor/RevertCaseDecisionStatusInteractor";

export interface TCaseDecisionDetailsPresenter extends TLoadingAwarePresenter {
  caseDecision: TCaseDecisionPresentationModel;
  organisations: IOrganisation[];
  caseDecisionTypes: TDocumentType[];
  caseDecisionIndexes: TOrganisationIndexPresentationModel[];
  attachmentTypes: TAttachmentTypePresentationModel[];
  attachmentContentTypes: IGenericCodebookItem[];
  caseDecisionAttachments: TAttachmentPresentationModel[];
  mainAttachment: TAttachmentPresentationModel;
  courtCase: ICourtCase;
  indexHierarchyList: string[][];
  user?: IUser;
  anonimisedUploadedFile: any;
  invalidatedCaseDecisions: ICaseDecision[];
}

export interface ICaseDecisionDetailsPresenter
  extends TCaseDecisionDetailsPresenter,
    TPresentable {
  loadCaseDecision(caseDecisions: ICaseDecision): void;
  loadReferenceValues(
    caseDecisionTypes: IDocumentType[],
    organisations: IOrganisation[],
    attachmentTypes: IAttachmentType[],
    attachmentContentTypes: IGenericCodebookItem[],
    courtCase: ICourtCase,
    invalidatedCaseDecisions: ICaseDecision[]
  ): void;
  loadAttachments(caseDecisionAttachments: IAttachment[]): void;
  loadCaseDecisionIndexes(caseDecisionIndexes: IOrganisationIndex[]);
  showCaseDecisionUpdatePage(id: string): void;
  archiveCaseDecision(caseDecision: ICaseDecision): void;
  publishCaseDecision(): void;
  canEdit(organisationId: number): boolean;
  removeCaseDecision(): void;
  setAnonymisedFileValue(file: any): void;
  showCaseDecisionDetailsPage(id: number): void;
  revertCaseDecisionStatus(): void;
}

const defaultState: TCaseDecisionDetailsPresenter = {
  caseDecision: {} as TCaseDecisionPresentationModel,
  organisations: [],
  caseDecisionTypes: [],
  caseDecisionIndexes: [],
  attachmentTypes: [],
  attachmentContentTypes: [],
  caseDecisionAttachments: [],
  indexHierarchyList: [],
  mainAttachment: {} as TAttachmentPresentationModel,
  courtCase: {} as ICourtCase,
  anonimisedUploadedFile: undefined,
  invalidatedCaseDecisions: []
};

const CaseDecisionDetailsPresenter = withStore<
  ICaseDecisionDetailsPresenter,
  TCaseDecisionDetailsPresenter
>(
  ({
    application,
    store,
    loader,
    translate
  }): ICaseDecisionDetailsPresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<TCaseDecisionDetailsPresenter>();
    loader.start("caseDecisionDetailsLoader");
    const loadCaseDecision = (item: ICaseDecision) => {
      const { organisations, caseDecisionTypes, attachmentTypes } =
        _store.getState<TCaseDecisionDetailsPresenter>();

      const caseDecisionType = caseDecisionTypes?.find(
        (value) => value.id === item.documentTypeId
      );
      const organisation = organisations?.find(
        (value) => value.id === item.organisationId
      );
      let attachment = item.attachment;
      let attachmentType = attachment
        ? attachmentTypes.find(
            (type) => type.id === attachment?.attachmentTypeId
          )
        : null;
      const mainAttachment =
        attachment && attachmentType
          ? AttachmentPresentationModel(attachment, attachmentType)
          : {};
      const caseDecision = CaseDecisionPresentationModel(
        item,
        caseDecisionType,
        organisation
      );
      _store.update({
        caseDecision,
        mainAttachment,
        breadCrumb: {
          id: () => caseDecision?.name
        }
      });
      loader.stop("caseDecisionDetailsLoader");
    };
    const loadReferenceValues = (
      caseDecisionTypes: IDocumentType[],
      organisations: IOrganisation[],
      attachmentTypesList: IAttachmentType[],
      attachmentContentTypes: IGenericCodebookItem[],
      courtCase: ICourtCase,
      invalidatedCaseDecisionList: ICaseDecision[]
    ) => {
      let attachmentTypes = (attachmentTypesList || []).map((item) => {
        const contentType = attachmentContentTypes?.find(
          (value) => value.id === item.attachmentContentTypeId
        );
        return AttachmentTypePresentationModel(item, contentType);
      });
      const user = _store.getState<any>().user;
      const invalidatedCaseDecisions = invalidatedCaseDecisionList.map(
        (item) => {
          const caseDecisionType = caseDecisionTypes?.find(
            (value) => value.id === item.documentTypeId
          );
          const organisation = organisations?.find(
            (value) => value.id === item.organisationId
          );
          return CaseDecisionPresentationModel(
            item,
            caseDecisionType,
            organisation
          );
        }
      );

      _store.update({
        caseDecisionTypes,
        organisations,
        attachmentContentTypes,
        attachmentTypes,
        user,
        courtCase,
        invalidatedCaseDecisions
      });
    };

    const loadCaseDecisionIndexes = (
      caseDecisionIndexes: IOrganisationIndex[]
    ) => {
      const { selectedLanguage } = _store.getState<any>();
      _store.update({
        caseDecisionIndexes: caseDecisionIndexes.map((item) =>
          OrganisationIndexPresentationModel(item, selectedLanguage)
        )
      });
      prepareHierarchyList();
    };

    const canEdit = (organisationId) => {
      const { user } = _store.getState<TCaseDecisionDetailsPresenter>();
      if (user) {
        return user.canEdit(organisationId);
      } else {
        return false;
      }
    };

    const getCorrectIndexTranslation = (index: any) => {
      const { selectedLanguage } = store.getState<any>();
      const nameTranslation = index.nameTranslations.find(
        (item) => item.languageId === selectedLanguage?.id
      )?.nameTranslation;
      return nameTranslation ?? index.name;
    };

    const getCorrectGroupTranslation = (data?: any) => {
      const { selectedLanguage } = store.getState<any>();
      if (data) {
        const nameTranslation = data.nameTranslations.find(
          (item) => item.languageId === selectedLanguage?.id
        )?.nameTranslation;
        return nameTranslation ?? data.name;
      } else {
        return "";
      }
    };

    const findParent = (group, groupList, indexHierarchyList) => {
      if (group?.parentId) {
        let g = groupList.find((item) => item.id === group?.parentId);
        if (g) {
          indexHierarchyList.push(getCorrectGroupTranslation(g));
          return findParent(g, groupList, indexHierarchyList);
        }
      } else if (group?.organisationIndexCategoryId) {
        indexHierarchyList.push(group?.categoryName);
      }
      return indexHierarchyList;
    };

    const prepareHierarchyList = () => {
      let indexHierarchyList = state.indexHierarchyList;
      const { caseDecisionIndexes } =
        _store.getState<TCaseDecisionDetailsPresenter>();
      caseDecisionIndexes.forEach((index) => {
        let indexHierarchy: string[] = [];
        let firstGroup = index.groups.find(
          (g) => g.id === index.organisationGroupId
        );
        indexHierarchy.push(getCorrectIndexTranslation(index));
        indexHierarchy.push(getCorrectGroupTranslation(firstGroup));
        indexHierarchy = findParent(firstGroup, index.groups, indexHierarchy);
        indexHierarchyList.push(indexHierarchy);
      });

      _store.update({ indexHierarchyList });
    };
    loader.start("attachmentLoader");
    const loadAttachments = (attachments: IAttachment[]) => {
      const { attachmentTypes } =
        _store.getState<TCaseDecisionDetailsPresenter>();

      let caseDecisionAttachments = (attachments || []).map((item) => {
        const attachmentType = attachmentTypes?.find(
          (value) => value.id === item.attachmentTypeId
        );
        return AttachmentPresentationModel(item, attachmentType);
      });
      _store.update({
        caseDecisionAttachments
      });
      loader.stop("attachmentLoader");
    };

    const showCaseDecisionUpdatePage = (id: string) => {
      return _application.navigator.push({
        pathname: `/case-decisions/${id.toString()}/update`
      });
    };

    const removeCaseDecision = async () => {
      const { caseDecision } = _store.getState<TCaseDecisionDetailsPresenter>();
      loader.start("caseDecisionDetailsLoader");
      await _application.container
        .resolve<RemoveCaseDecisionInteractor>("removeCaseDecision")
        .execute(caseDecision.id);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
      loader.stop("caseDecisionDetailsLoader");
      return _application.navigator.push({
        pathname: `/search?collection=ODLUKA&q=`
      });
    };

    const archiveCaseDecision = async (caseDecision: ICaseDecision) => {
      loader.start("caseDecisionDetailsLoader");
      const { organisations, caseDecisionTypes } =
        _store.getState<TCaseDecisionDetailsPresenter>();
      await _application.container
        .resolve<ArchiveCaseDecisionInteractor>("archiveCaseDecision")
        .execute(caseDecision);
      let item = await _application.container
        .resolve<FindCaseDecisionInteractor>("findCaseDecision")
        .execute(caseDecision.id);
      const caseDecisionType = caseDecisionTypes?.find(
        (value) => value.id === item.documentTypeId
      );
      const organisation = organisations?.find(
        (value) => value.id === item.organisationId
      );
      let model = CaseDecisionPresentationModel(
        item,
        caseDecisionType,
        organisation
      );
      _store.update({ caseDecision: model });
      loader.stop("caseDecisionDetailsLoader");
    };

    const setAnonymisedFileValue = (file: any) => {
      _store.update({ anonimisedUploadedFile: file });
    };

    const showCaseDecisionDetailsPage = (id: number) => {
      return _application.navigator.push({
        pathname: `/case-decisions/${id.toString()}/details`
      });
    };

    const publishCaseDecision = async () => {
      const { caseDecision, caseDecisionTypes, organisations } =
        _store.getState<TCaseDecisionDetailsPresenter>();

      await _application.container
        .resolve<PublishCaseDecisionInteractor>("publishCaseDecision")
        .execute(caseDecision.id);
      let item = await _application.container
        .resolve<FindCaseDecisionInteractor>("findCaseDecision")
        .execute(caseDecision.id);
      const caseDecisionType = caseDecisionTypes?.find(
        (value) => value.id === item.documentTypeId
      );
      const organisation = organisations?.find(
        (value) => value.id === item.organisationId
      );
      let model = CaseDecisionPresentationModel(
        item,
        caseDecisionType,
        organisation
      );
      _store.update({ caseDecision: model });
      loader.stop("caseDecisionDetailsLoader");
    };

    const revertCaseDecisionStatus = async () => {
      const { caseDecision, caseDecisionTypes, organisations } =
        _store.getState<TCaseDecisionDetailsPresenter>();
      loader.start("caseDecisionDetailsLoader");
      await _application.container
        .resolve<RevertCaseDecisionStatusInteractor>("revertCaseDecisionStatus")
        .execute(caseDecision.id);
      let item = await _application.container
        .resolve<FindCaseDecisionInteractor>("findCaseDecision")
        .execute(caseDecision.id);
      const caseDecisionType = caseDecisionTypes?.find(
        (value) => value.id === item.documentTypeId
      );
      const organisation = organisations?.find(
        (value) => value.id === item.organisationId
      );
      let model = CaseDecisionPresentationModel(
        item,
        caseDecisionType,
        organisation
      );
      _store.update({ caseDecision: model });
      loader.stop("caseDecisionDetailsLoader");
    };

    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      loadCaseDecision,
      loadReferenceValues,
      loadCaseDecisionIndexes,
      loadAttachments,
      showCaseDecisionUpdatePage,
      archiveCaseDecision,
      canEdit,
      removeCaseDecision,
      setAnonymisedFileValue,
      showCaseDecisionDetailsPage,
      publishCaseDecision,
      revertCaseDecisionStatus
    };
  },
  defaultState
);

export default CaseDecisionDetailsPresenter;
